import { CarouselSection } from '@app/components/CarouselSection';
import {
  CarouselSectionContext,
  CarouselSectionContextValue,
} from '@app/components/CarouselSection/carouselSectionContext';
import { SimilarDesignItem } from '@app/data/clients/rbServerSideClient/queries/GetSimilarDesigns/types';
import {
  AnalyticsComponentName,
  parseSimilarDesignItemToAnalyticsEcommerceItem,
  trackGA4SelectItemEvent,
  trackGA4ViewItemListEvent,
} from '@app/pages/i/analytics';
import {
  PreviewStyle,
  SIMILAR_DESIGNS_LIMIT,
  SIMILAR_DESIGNS_SEO_LINK_LIMIT,
} from '@app/pages/i/constants';
import { ProductPageProps } from '@app/pages/i/types';
import type { Context as SimilarDesignsContext } from '@app/pages/i/utils/SimilarDesignsContext';
import { SearchUUIDStorageKey } from '@app/utils/searchUUIDStorage';
import { Box, Skeleton, Slide, Text } from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { AppProps, UserInfo } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
  AnalyticsItemsListIDNameMap,
} from '@redbubble/next-analytics/gtm';
import {
  useNavigation,
  usePageProps,
  useUserInfo,
} from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import { noop } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import styles from './OffsiteSimilarDesignsSection.module.scss';
import { getUsableSimilarDesigns, mapMediaCardProps } from './utils';

const messages = defineMessages({
  artworkMessage: {
    defaultMessage: 'Other {labelText} designs you might like',
  },
  productMessage: {
    defaultMessage: 'You might also like...',
  },
});

interface OffsiteSimilarDesignsCarouselProps {
  similarDesignsLoading: SimilarDesignsContext['similarDesignsLoading'];
  similarDesigns: SimilarDesignsContext['similarDesignsData']['similarDesigns'];
  refreshSimilarDesigns: SimilarDesignsContext['refreshSimilarDesigns'];
}

export const OffsiteSimilarDesignsSection: React.FC<
  OffsiteSimilarDesignsCarouselProps
> = ({ similarDesigns, similarDesignsLoading, refreshSimilarDesigns }) => {
  const [userInfo] = useUserInfo<UserInfo>();
  const intl = useIntl();
  const { navigate, loading: clientSideRouteLoading } = useNavigation();
  const { isBot, groupId, iaEntityCopy } = usePageProps<
    AppProps & ProductPageProps
  >();

  const handleNavigate = useCallback(
    (
      event: MouseEvent,
      link: string,
      inventoryItem: NonNullable<SimilarDesignItem['inventoryItem']>,
      index: number
    ) => {
      event.preventDefault();
      trackGA4SelectItemEvent({
        item: inventoryItem,
        itemListParams: {
          itemIndex: index,
          itemListID: AnalyticsItemsList.OffsiteSimilarDesigns,
          itemListName:
            AnalyticsItemsListIDNameMap.offsite_similar_designs_carousel,
        },
      });
      navigate({ url: link });
      const { locale, country, currency } = userInfo;
      refreshSimilarDesigns({
        inventoryItemId: inventoryItem?.id || '',
        limit: isBot ? SIMILAR_DESIGNS_SEO_LINK_LIMIT : SIMILAR_DESIGNS_LIMIT,
        locale,
        country,
        currency,
        previewTypeIds: [
          groupId
            ? PreviewStyle.ALTERNATE_PRODUCT_CLOSE
            : PreviewStyle.CLOSE_PREVIEW,
        ],
        groupId,
        isBot,
      });
    },
    [navigate, userInfo, refreshSimilarDesigns, isBot, groupId]
  );

  const contextValue: CarouselSectionContextValue = useMemo(() => {
    return {
      mediaCardPropsMapper: mapMediaCardProps(handleNavigate),
      analytics: {
        componentName: AnalyticsComponentName.OffsiteSimilarDesigns,
        listID: AnalyticsItemsList.OffsiteSimilarDesigns,
        inViewTracking: () => {
          const analyticsItems = similarDesigns
            .map((similarDesignItem, index) => {
              return parseSimilarDesignItemToAnalyticsEcommerceItem({
                similarDesignItem,
                index,
              });
            })
            .filter(
              (
                item: AnalyticsEcommerceItem | null
              ): item is NonNullable<AnalyticsEcommerceItem> => !!item
            );

          trackGA4ViewItemListEvent({
            items: analyticsItems,
            listID: AnalyticsItemsList.OffsiteSimilarDesigns,
            topic: '',
            searchUUIDStorageKey: SearchUUIDStorageKey.LocationPathname,
          });
        },
      },
      rsLocation: RSProductClickedLocation.OffsiteSimilarDesigns,
      rsPageType: RSPageType.Product,
    };
  }, [similarDesigns, handleNavigate]);

  const getSlides = () => {
    if (!similarDesignsLoading && similarDesigns.length > 0) {
      return getUsableSimilarDesigns(similarDesigns).map(
        (recommendation: SimilarDesignItem, index: number) => {
          return (
            <CarouselSection.CarouselSlide
              key={`sd-slide-${recommendation.inventoryItemId}`}
            >
              <CarouselSection.CarouselMediaCard
                data={recommendation}
                key={recommendation.inventoryItemId}
                index={index}
              />
            </CarouselSection.CarouselSlide>
          );
        }
      );
    }

    return null;
  };

  const label = iaEntityCopy?.label;

  if (!similarDesignsLoading && similarDesigns.length === 0) {
    return null;
  }

  return (
    <CarouselSectionContext.Provider value={contextValue}>
      <CarouselSection.Container>
        <Box className={styles.container}>
          {label && (
            <Box className={styles.headerSection}>
              <Box marginBottom="xxs" data-testid="test-id">
                <Text element="h2" type="display5">
                  {intl.formatMessage(messages.artworkMessage, {
                    labelText: label,
                  })}
                </Text>
              </Box>
            </Box>
          )}
          <Box className={styles.carousel}>
            <CarouselSection.Carousel
              // Key to force re-render when client side loading, to reset carousel to show the first slide
              key={`offsite-carousel-${similarDesigns.length}`}
              itemsInView={{
                mobile: 3,
                tablet: 5,
                desktop: 6,
              }}
            >
              {!similarDesignsLoading && !clientSideRouteLoading
                ? getSlides()
                : Array.from({ length: 8 }).map(() => (
                    <Slide inView key={Math.random()} onUnmount={noop}>
                      <Box className={styles.skeleton}>
                        <Skeleton
                          height="100%"
                          width="100%"
                          animated
                          roundedCorners
                          data-testid="offsite-skeleton-card"
                        />
                      </Box>
                    </Slide>
                  ))}
            </CarouselSection.Carousel>
          </Box>
        </Box>
      </CarouselSection.Container>
    </CarouselSectionContext.Provider>
  );
};
